import { useState, useEffect } from "react";
import { loadStripe } from "@stripe/stripe-js";
import { Elements } from "@stripe/react-stripe-js";
import { useLanguage } from "@/lib/language-context";
import { useApp } from "@/lib/app-context";
import { visaService } from "@/lib/visa-service";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Check, Building, CreditCard, Phone, Copy, ChevronDown, ChevronUp, FileText } from "lucide-react";
import { CardPaymentForm } from "./card-payment-form";

type PaymentMethod = "bank" | "card";

type VisaType = "30_days" | "1_year" | "ebusiness_1_year";

export function PaymentScreen() {
  const { t } = useLanguage();
  const { setCurrentScreen, applicationId, visaType: contextVisaType } = useApp();
  const { toast } = useToast();
  const [selectedPayment, setSelectedPayment] = useState<PaymentMethod>("bank");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isConfirmingPayment, setIsConfirmingPayment] = useState(false);
  const [visaType, setVisaType] = useState<VisaType | null>(null);
  const [isRefundPolicyOpen, setIsRefundPolicyOpen] = useState(false);
  // Stripe card flow: after createPaymentIntent we show Elements
  const [cardClientSecret, setCardClientSecret] = useState<string | null>(null);
  const [stripePromise, setStripePromise] = useState<ReturnType<typeof loadStripe> | null>(null);

  useEffect(() => {
    // Use visa_type from context if available (set during submission), otherwise fetch
    if (contextVisaType) {
      setVisaType(contextVisaType as VisaType);
      return;
    }
    
    // Only fetch if not in context and we have an applicationId
    if (!applicationId) return;
    visaService
      .getApplication(applicationId)
      .then((app) => setVisaType((app.visa_type as VisaType) || null))
      .catch(() => setVisaType(null));
  }, [applicationId, contextVisaType]);

  const displayPrice =
    visaType === "30_days"
      ? t.price30Days
      : visaType === "1_year"
        ? t.price1Year
        : visaType === "ebusiness_1_year"
          ? t.priceEBusiness1Year
          : t.price;

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
  };

  const handleSubmit = async () => {
    if (!selectedPayment || !applicationId) return;

    if (selectedPayment === "card") {
      // Card: create PaymentIntent then show Stripe form
      if (cardClientSecret) return; // form is handling confirm
      setIsSubmitting(true);
      try {
        const res = await visaService.createPaymentIntent(applicationId);
        setCardClientSecret(res.client_secret);
        setStripePromise(loadStripe(res.stripe_publishable_key));
        try {
          sessionStorage.setItem("vimana_payment_application_id", applicationId);
        } catch (_) {}
      } catch (err) {
        console.error("Create payment intent failed:", err);
        toast({
          title: t.cardPaymentError,
          description: err instanceof Error ? err.message : undefined,
          variant: "destructive",
        });
      } finally {
        setIsSubmitting(false);
      }
      return;
    }

    // Bank: update payment method and go to success
    setIsSubmitting(true);
    try {
      await visaService.updatePayment(applicationId, {
        payment_method: selectedPayment,
      });
      setCurrentScreen("apply-success");
    } catch (error) {
      console.error("Failed to update payment:", error);
      toast({
        title: t.bankPaymentError,
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCardPaymentSuccess = async () => {
    if (!applicationId) return;
    setIsConfirmingPayment(true);
    try {
      await visaService.updatePayment(applicationId, { payment_method: "card" });
      toast({
        title: t.paymentSuccessToast,
        variant: "default",
      });
      setCurrentScreen("apply-success");
    } catch {
      toast({ title: t.cardPaymentError, variant: "destructive" });
    } finally {
      setIsConfirmingPayment(false);
    }
  };

  const handleCardPaymentError = (message?: string) => {
    toast({
      title: t.cardPaymentError,
      description: message ?? undefined,
      variant: "destructive",
    });
  };

  return (
    <div className="flex flex-col min-h-[calc(100vh-80px)]">
      {/* Progress Indicator - Simple dots */}
      <div className="px-4 py-3 bg-secondary">
        <div className="max-w-lg mx-auto flex items-center justify-center gap-3">
          <div className="w-3 h-3 rounded-full bg-primary" />
          <div className="w-8 h-0.5 bg-primary" />
          <div className="w-3 h-3 rounded-full bg-primary" />
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 px-4 py-6">
        <div className="max-w-lg mx-auto">
          <h2 className="text-2xl font-bold text-foreground mb-2 text-center">
            {t.paymentTitle}
          </h2>
          <p className="text-muted-foreground text-center mb-4">{t.paymentDesc}</p>
          
          {/* Application ID Display */}
          {applicationId && (
            <div className="mb-6 text-center">
              <div className="inline-block bg-primary/10 px-4 py-2 rounded-lg border border-primary/20">
                <p className="text-sm text-muted-foreground">{t.applicationId}</p>
                <p className="text-lg font-bold text-primary">{applicationId}</p>
              </div>
            </div>
          )}

          {/* Total Amount Card */}
          <Card className="p-6 mb-6 bg-primary text-primary-foreground text-center border-0">
            <p className="text-lg mb-2">{t.totalAmount}</p>
            <p className="text-4xl font-bold">{displayPrice}</p>
          </Card>

          {/* Refund Policy Section */}
          <Card className="mb-6 overflow-hidden">
            <button
              onClick={() => setIsRefundPolicyOpen(!isRefundPolicyOpen)}
              className="w-full p-4 flex items-center justify-between text-left hover:bg-secondary/50 transition-colors"
            >
              <div className="flex items-center gap-3">
                <FileText className="h-5 w-5 text-primary flex-shrink-0" />
                <h3 className="text-lg font-bold text-foreground">{t.refundPolicyTitle}</h3>
              </div>
              {isRefundPolicyOpen ? (
                <ChevronUp className="h-5 w-5 text-primary" />
              ) : (
                <ChevronDown className="h-5 w-5 text-primary" />
              )}
            </button>
            
            {isRefundPolicyOpen && (
              <div className="px-4 pb-4 space-y-4">
                <p className="text-sm text-muted-foreground leading-relaxed">
                  {t.refundPolicyIntro}
                </p>

                <div className="space-y-3">
                  <div>
                    <h4 className="font-bold text-foreground mb-2">{t.refundPolicyVisaRejection}</h4>
                    <p className="text-sm text-muted-foreground mb-2">{t.refundPolicyVisaRejectionDesc}</p>
                    <ul className="text-sm text-muted-foreground space-y-1 ml-4 list-disc">
                      <li>{t.refundPolicyServiceFeeRefundable}</li>
                      <li>{t.refundPolicyEmbassyFeeNonRefundable}</li>
                    </ul>
                  </div>

                  <div>
                    <h4 className="font-bold text-foreground mb-3">{t.refundPolicyBreakdown}</h4>
                    <div className="space-y-3">
                      {/* 1 Year Visa */}
                      <div className="bg-muted p-3 rounded-lg">
                        <p className="font-semibold text-foreground mb-2">{t.refundPolicy1YearTitle}</p>
                        <div className="text-sm text-muted-foreground space-y-1">
                          <p>{t.refundPolicy1YearTotal}</p>
                          <p className="text-green-600 dark:text-green-400">{t.refundPolicy1YearRefundable}</p>
                          <p className="text-red-600 dark:text-red-400">{t.refundPolicy1YearNonRefundable}</p>
                        </div>
                      </div>

                      {/* 1 Month Visa */}
                      <div className="bg-muted p-3 rounded-lg">
                        <p className="font-semibold text-foreground mb-2">{t.refundPolicy1MonthTitle}</p>
                        <div className="text-sm text-muted-foreground space-y-1">
                          <p>{t.refundPolicy1MonthTotal}</p>
                          <p className="text-green-600 dark:text-green-400">{t.refundPolicy1MonthRefundable}</p>
                          <p className="text-red-600 dark:text-red-400">{t.refundPolicy1MonthNonRefundable}</p>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="bg-primary/10 p-3 rounded-lg border border-primary/20">
                    <h4 className="font-bold text-foreground mb-2">{t.refundPolicyImportantNotes}</h4>
                    <ul className="text-sm text-muted-foreground space-y-1 ml-4 list-disc">
                      <li>{t.refundPolicyNote1}</li>
                      <li>{t.refundPolicyNote2}</li>
                    </ul>
                  </div>

                  <div className="pt-2 border-t border-border">
                    <p className="text-sm text-muted-foreground italic">
                      {t.refundPolicyClarification}
                    </p>
                  </div>
                </div>
              </div>
            )}
          </Card>

          {/* Payment Methods */}
          <h3 className="text-lg font-bold text-foreground mb-4">{t.paymentMethods}</h3>

          {/* Stripe card form (after PaymentIntent created) */}
          {selectedPayment === "card" && cardClientSecret && stripePromise && (
            <Card className="p-5 mb-6 border-2 border-primary bg-secondary">
              {isConfirmingPayment ? (
                <p className="text-center text-muted-foreground py-4">{t.paymentConfirming}</p>
              ) : (
                <Elements stripe={stripePromise} options={{ clientSecret: cardClientSecret }}>
                  <CardPaymentForm onSuccess={handleCardPaymentSuccess} onError={handleCardPaymentError} />
                </Elements>
              )}
            </Card>
          )}

          {/* Bank Transfer */}
          <Card
            onClick={() => setSelectedPayment("bank")}
            className={`p-5 mb-4 cursor-pointer transition-all border-2 ${
              selectedPayment === "bank" ? "border-primary bg-secondary" : "border-transparent hover:border-primary/30"
            }`}
          >
            <div className="flex items-start gap-4">
              <div className="flex-shrink-0 w-14 h-14 rounded-full bg-muted flex items-center justify-center">
                <Building className="h-7 w-7 text-primary" />
              </div>
              <div className="flex-1">
                <h4 className="text-lg font-bold text-foreground mb-1">{t.bankTransfer}</h4>
                <p className="text-sm text-muted-foreground mb-4">{t.payLater}</p>
                {selectedPayment === "bank" && (
                  <div className="space-y-3 text-sm">
                    <div className="flex items-center justify-between bg-background p-3 rounded-lg border">
                      <div>
                        <p className="text-muted-foreground">Bank Name</p>
                        <p className="font-semibold">Commercial Bank </p>
                      </div>
                      <Copy className="h-4 w-4 cursor-pointer text-primary" onClick={() => copyToClipboard("Commercial Bank")} />
                    </div>
                    <div className="flex items-center justify-between bg-background p-3 rounded-lg border">
                      <div>
                        <p className="text-muted-foreground">Branch</p>
                        <p className="font-semibold">Wellawatte</p>
                      </div>
                      <Copy className="h-4 w-4 cursor-pointer text-primary" onClick={() => copyToClipboard("Wellawatte")} />
                    </div>
                    <div className="flex items-center justify-between bg-background p-3 rounded-lg border">
                      <div>
                        <p className="text-muted-foreground">Account Holder</p>
                        <p className="font-semibold">HUEX (PVT) LTD</p>
                      </div>
                      <Copy className="h-4 w-4 cursor-pointer text-primary" onClick={() => copyToClipboard("HUEX (PVT) LTD")} />
                    </div>
                    <div className="flex items-center justify-between bg-background p-3 rounded-lg border">
                      <div>
                        <p className="text-muted-foreground">Account Number</p>
                        <p className="font-semibold tracking-wider">1000243855</p>
                      </div>
                      <Copy className="h-4 w-4 cursor-pointer text-primary" onClick={() => copyToClipboard("1000243855")} />
                    </div>
                  </div>
                )}
              </div>
              <div className={`flex-shrink-0 w-7 h-7 rounded-full border-2 flex items-center justify-center mt-1 ${selectedPayment === "bank" ? "bg-primary border-primary" : "border-muted-foreground"}`}>
                {selectedPayment === "bank" && <Check className="h-4 w-4 text-primary-foreground" />}
              </div>
            </div>
          </Card>

          {/* Pay by Card */}
          <Card
            onClick={() => setSelectedPayment("card")}
            className={`p-5 mb-6 cursor-pointer transition-all border-2 ${
              selectedPayment === "card" ? "border-primary bg-secondary" : "border-transparent hover:border-primary/30"
            }`}
          >
            <div className="flex items-center gap-4">
              <div className="flex-shrink-0 w-14 h-14 rounded-full bg-muted flex items-center justify-center">
                <CreditCard className="h-7 w-7 text-primary" />
              </div>
              <div className="flex-1">
                <h4 className="text-lg font-bold text-foreground">{t.paymentCard}</h4>
                <p className="text-sm text-muted-foreground">{t.cardSecure}</p>
              </div>
              <div className={`flex-shrink-0 w-7 h-7 rounded-full border-2 flex items-center justify-center ${selectedPayment === "card" ? "bg-primary border-primary" : "border-muted-foreground"}`}>
                {selectedPayment === "card" && <Check className="h-4 w-4 text-primary-foreground" />}
              </div>
            </div>
          </Card>

          {/* Cash Payment (commented out) */}
          {/* <Card
            onClick={() => setSelectedPayment("cash")}
            className={`p-5 mb-6 cursor-pointer transition-all border-2 ${
              selectedPayment === "cash"
                ? "border-primary bg-secondary"
                : "border-transparent hover:border-primary/30"
            }`}
          >
            <div className="flex items-center gap-4">
              <div className="flex-shrink-0 w-14 h-14 rounded-full bg-muted flex items-center justify-center">
                <Banknote className="h-7 w-7 text-primary" />
              </div>
              <div className="flex-1">
                <h4 className="text-lg font-bold text-foreground">{t.cashPayment}</h4>
                <p className="text-sm text-muted-foreground">{t.payLater}</p>
              </div>
              <div
                className={`flex-shrink-0 w-7 h-7 rounded-full border-2 flex items-center justify-center ${
                  selectedPayment === "cash" ? "bg-primary border-primary" : "border-muted-foreground"
                }`}
              >
                {selectedPayment === "cash" && <Check className="h-4 w-4 text-primary-foreground" />}
              </div>
            </div>
          </Card> */}

          {/* Help Card */}
          <Card className="p-4 bg-muted border-0">
            <div className="flex items-center gap-3">
              <Phone className="h-5 w-5 text-primary flex-shrink-0" />
              <p className="text-sm text-muted-foreground">
                {t.contactDesc}:{" "}
                <a href={`tel:+${t.phoneNumberRaw}`} className="text-primary font-bold">
                  {t.phoneNumber}
                </a>
              </p>
            </div>
          </Card>
        </div>
      </div>

      {/* Bottom CTA (hidden when card form is shown) */}
      {!(selectedPayment === "card" && cardClientSecret) && (
        <div className="sticky bottom-0 p-4 bg-card border-t border-border safe-area-inset-bottom">
          <div className="max-w-lg mx-auto">
            <Button
              onClick={handleSubmit}
              disabled={!selectedPayment || isSubmitting}
              size="lg"
              className="w-full h-14 text-xl font-bold rounded-xl"
            >
              {isSubmitting
                ? t.loading
                : selectedPayment === "card"
                  ? t.payWithCard
                  : t.submitApplication}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
